import { z } from "zod";
import {
  DATE_PATTERN,
  TIME_PATTERN,
  cta,
  href,
  id,
  image,
  price,
  requiredText,
  text,
  youtubeUrl,
} from "./primitives";

/**
 * Section catalogue: every section type with its allowed variants and a strict content schema.
 * Adding a section type = add an entry here + a component in @baze/engine + defaults in templates.
 */

const eyebrow = text(60).optional();
const dataSource = z.strictObject({ type: z.literal("static") }).optional();

const items = <T extends z.ZodType>(item: T, max: number) => z.array(item).max(max);

export const sectionContent = {
  navbar: z.strictObject({
    ctaLabel: text(40).optional(),
    showCta: z.boolean().optional(),
    /** Fixed bottom bar with the nav links on phones (app-style). */
    mobileBar: z.boolean().optional(),
  }),
  hero: z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    image: image().optional(),
    /** Extra slides after `image`. Only the slideshow variant shows them. */
    images: items(image(), 8).optional(),
    primaryCta: cta().optional(),
    secondaryCta: cta().optional(),
  }),
  about: z.strictObject({
    eyebrow,
    title: requiredText(120),
    body: text(1500),
    image: image().optional(),
    highlights: items(requiredText(80), 6).optional(),
  }),
  services: z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    items: items(
      z.strictObject({
        title: requiredText(80),
        description: text(320).optional(),
        price: price().optional(),
        priceNote: text(40).optional(),
        image: image().optional(),
      }),
      24,
    ),
  }),
  menu: z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    categories: items(
      z.strictObject({
        name: requiredText(60),
        items: items(
          z.strictObject({
            name: requiredText(80),
            description: text(240).optional(),
            price: price().optional(),
            image: image().optional(),
            tags: items(requiredText(20), 4).optional(),
          }),
          40,
        ),
      }),
      12,
    ),
    orderMessage: text(300).optional(),
    dataSource,
  }),
  products: z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    items: items(
      z.strictObject({
        name: requiredText(80),
        description: text(320).optional(),
        price: price().optional(),
        priceNote: text(40).optional(),
        image: image().optional(),
        badge: text(20).optional(),
        /** Artwork / handmade details (all optional): shown as a spec list on the card. */
        artist: text(80).optional(),
        medium: text(80).optional(),
        size: text(40).optional(),
        year: text(10).optional(),
        /** Availability. "sold" shows a chip and hides the order button. */
        status: z.enum(["available", "reserved", "sold"]).optional(),
      }),
      48,
    ),
    orderMessage: text(300).optional(),
    dataSource,
  }),
  gallery: z.strictObject({
    eyebrow,
    title: text(120).optional(),
    images: items(image(), 24),
  }),
  testimonials: z.strictObject({
    eyebrow,
    title: requiredText(120),
    items: items(
      z.strictObject({
        quote: requiredText(500),
        name: requiredText(60),
        role: text(60).optional(),
        rating: z.number().int().min(1).max(5).optional(),
        /** True for placeholder content shipped by a template. Publishing warns while any remain. */
        sample: z.boolean().optional(),
      }),
      12,
    ),
  }),
  faq: z.strictObject({
    eyebrow,
    title: requiredText(120),
    items: items(z.strictObject({ question: requiredText(160), answer: requiredText(800) }), 20),
  }),
  stats: z.strictObject({
    eyebrow,
    title: text(120).optional(),
    items: items(z.strictObject({ value: requiredText(20), label: requiredText(60) }), 6),
  }),
  "opening-hours": z.strictObject({
    eyebrow,
    title: requiredText(120),
    note: text(200).optional(),
  }),
  location: z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    showMap: z.boolean().optional(),
  }),
  contact: z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    message: text(300).optional(),
    showWhatsapp: z.boolean().optional(),
    showPhone: z.boolean().optional(),
    showEmail: z.boolean().optional(),
    showSocial: z.boolean().optional(),
  }),
  pricing: z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    plans: items(
      z.strictObject({
        name: requiredText(60),
        price: price().optional(),
        priceNote: text(40).optional(),
        period: text(20).optional(),
        features: items(requiredText(80), 12),
        highlighted: z.boolean().optional(),
        cta: cta().optional(),
      }),
      6,
    ),
  }),
  cta: z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    button: cta(),
  }),
  timeline: z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    items: items(
      z.strictObject({
        /** Short marker: a step number, a year, or a date ("Langkah 1", "2019", "Juni 2024"). */
        label: text(40).optional(),
        title: requiredText(80),
        description: text(400).optional(),
        image: image().optional(),
      }),
      12,
    ),
  }),
  events: z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    items: items(
      z.strictObject({
        name: requiredText(60),
        date: z.string().regex(DATE_PATTERN).optional(),
        startTime: z.string().regex(TIME_PATTERN).optional(),
        endTime: z.string().regex(TIME_PATTERN).optional(),
        /** Free text after the time, e.g. "WIB", "s.d. selesai". */
        timeNote: text(40).optional(),
        venue: text(120).optional(),
        address: text(240).optional(),
        mapUrl: href().optional(),
        description: text(320).optional(),
      }),
      6,
    ),
    /** Show an "add to Google Calendar" link on dated events. */
    showCalendar: z.boolean().optional(),
  }),
  team: z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    items: items(
      z.strictObject({
        name: requiredText(80),
        role: text(80).optional(),
        description: text(320).optional(),
        image: image().optional(),
        instagram: z
          .string()
          .regex(/^[A-Za-z0-9._]{1,30}$/)
          .optional(),
      }),
      12,
    ),
  }),
  quote: z.strictObject({
    eyebrow,
    text: requiredText(600),
    cite: text(120).optional(),
  }),
  countdown: z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    /** The moment counted down to: local wall-clock date and time in the zone from `timeNote`. */
    date: z.string().regex(DATE_PATTERN),
    time: z.string().regex(TIME_PATTERN).optional(),
    /** "WIB" / "WITA" / "WIT" picks the zone (WIB by default). */
    timeNote: text(40).optional(),
    /** Shown once the moment has passed. */
    doneText: text(120).optional(),
    showCalendar: z.boolean().optional(),
  }),
  gift: z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    accounts: items(
      z.strictObject({
        provider: requiredText(40),
        name: requiredText(60),
        number: z
          .string()
          .min(1)
          .max(40)
          .regex(/^[0-9A-Za-z .+-]+$/),
      }),
      6,
    ),
    /** Address for physical gifts. */
    address: text(300).optional(),
  }),
  /** Guestbook: visitors leave a name and a message; the live page loads and posts them at runtime. */
  wishes: z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    /** Also ask "Hadir / Tidak hadir" next to the message (for invitations without an RSVP section). */
    askAttendance: z.boolean().optional(),
  }),
  /** Attendance confirmation: name, attending, number of people. One answer per name, editable by the guest. */
  rsvp: z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    /** Last day to answer (local date); the form closes after it. */
    deadline: z.string().regex(DATE_PATTERN).optional(),
    /** Most people one answer may bring, including the guest (default 2). */
    maxPax: z.number().int().min(1).max(10).optional(),
    /** Show a free-text note field (dietary needs, a message). */
    askNote: z.boolean().optional(),
  }),
  /** Newest blog posts (the posts live outside the definition; the runtime passes them in). */
  "blog-latest": z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    /** How many posts (default 3). */
    count: z.number().int().min(1).max(6).optional(),
    /** Text of the link to /blog, e.g. "Semua artikel". */
    linkLabel: text(40).optional(),
  }),
  links: z.strictObject({
    eyebrow,
    title: text(120).optional(),
    items: items(
      z.strictObject({
        label: requiredText(60),
        href: href(),
        description: text(120).optional(),
        /** Shown in the primary colour, e.g. the newest video or main shop. */
        highlight: z.boolean().optional(),
        /** Affiliate / paid link: rel="sponsored" and an "Afiliasi" badge. */
        sponsored: z.boolean().optional(),
      }),
      20,
    ),
  }),
  picks: z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    /** Affiliate links (default true): rel="sponsored" and a visible disclosure line. */
    affiliate: z.boolean().optional(),
    disclosure: text(240).optional(),
    /** Button text, e.g. "Lihat proyek". Defaults to "Lihat di <toko>". */
    linkLabel: text(30).optional(),
    items: items(
      z.strictObject({
        name: requiredText(80),
        description: text(240).optional(),
        image: image().optional(),
        price: price().optional(),
        priceNote: text(40).optional(),
        href: href(),
        store: text(30).optional(),
        badge: text(20).optional(),
      }),
      24,
    ),
  }),
  /**
   * Learning material: groups (Pertemuan, Modul, Bab) of topics. Each topic opens in place as a
   * detail view (its own #link) with text, a checklist, a PDF link and a YouTube video.
   */
  lessons: z.strictObject({
    eyebrow,
    title: requiredText(120),
    description: text(320).optional(),
    /** Search box and group filter chips above the list (both default on). */
    showSearch: z.boolean().optional(),
    showFilter: z.boolean().optional(),
    searchPlaceholder: text(60).optional(),
    /** Button texts, e.g. "Buka PDF", "Tonton di YouTube", "Kembali". */
    pdfLabel: text(30).optional(),
    videoLabel: text(30).optional(),
    backLabel: text(30).optional(),
    /** Reminder shown at the end of every topic, e.g. "Ingat, Bunda". */
    note: z.strictObject({ title: text(60).optional(), body: requiredText(400) }).optional(),
    groups: items(
      z.strictObject({
        /** Short marker: "Pertemuan 1", "Modul 2", "Bab 3". */
        label: text(30).optional(),
        title: requiredText(80),
        description: text(240).optional(),
        topics: items(
          z.strictObject({
            title: requiredText(100),
            summary: text(320).optional(),
            image: image().optional(),
            points: items(requiredText(120), 10).optional(),
            body: text(4000).optional(),
            /** Link to the PDF (e.g. Google Drive). */
            pdf: href().optional(),
            /** YouTube video, embedded in the topic. */
            video: youtubeUrl().optional(),
          }),
          30,
        ),
      }),
      12,
    ),
  }),
  footer: z.strictObject({
    text: text(300).optional(),
    showSocial: z.boolean().optional(),
    showHours: z.boolean().optional(),
    showAddress: z.boolean().optional(),
  }),
} as const;

export type SectionType = keyof typeof sectionContent;
export const SECTION_TYPES = Object.keys(sectionContent) as SectionType[];

export const SECTION_VARIANTS = {
  navbar: ["simple", "centered", "floating"],
  hero: [
    "split",
    "centered",
    "image-background",
    "minimal",
    "arch",
    "editorial",
    "profile",
    "celebration",
    "cover",
    "marquee",
    "collage",
    "stage",
    "margin",
    "envelope",
    "boarding-pass",
    "newspaper",
    "glasshouse",
    "slideshow",
  ],
  about: ["split", "centered", "feature"],
  services: ["cards", "list", "alternating", "index"],
  menu: ["cards", "list"],
  products: ["grid", "cards"],
  gallery: ["grid", "mosaic", "carousel", "masonry", "polaroid", "slideshow"],
  testimonials: ["cards", "single", "carousel", "chat"],
  faq: ["accordion", "two-column"],
  stats: ["row", "cards"],
  "opening-hours": ["table", "card"],
  location: ["card", "map"],
  contact: ["whatsapp-card", "split"],
  pricing: ["cards", "table"],
  cta: ["banner", "split", "spotlight", "celebration", "marquee"],
  timeline: ["vertical", "alternating"],
  events: ["cards", "stacked"],
  team: ["grid", "couple"],
  quote: ["centered", "ornate"],
  countdown: ["boxes", "minimal"],
  gift: ["cards"],
  wishes: ["cards", "stream"],
  rsvp: ["card", "split"],
  "blog-latest": ["cards", "list"],
  links: ["stack", "grid"],
  picks: ["grid", "list", "carousel", "bento"],
  lessons: ["cards", "list"],
  footer: ["simple", "columns"],
} as const satisfies Record<SectionType, readonly [string, ...string[]]>;

export type SectionVariant<T extends SectionType = SectionType> = (typeof SECTION_VARIANTS)[T][number];

export const SECTION_TONES = ["default", "surface", "inverted"] as const;
export type SectionTone = (typeof SECTION_TONES)[number];

const sectionFor = <T extends SectionType>(type: T) =>
  z.strictObject({
    id: id(),
    type: z.literal(type),
    variant: z.enum(SECTION_VARIANTS[type] as unknown as [string, ...string[]]),
    /** URL fragment for in-page navigation. Unique per page. */
    anchor: id(),
    navLabel: text(30).optional(),
    showInNav: z.boolean().optional(),
    hidden: z.boolean().optional(),
    tone: z.enum(SECTION_TONES).optional(),
    content: sectionContent[type],
  });

export const sectionSchema = z.discriminatedUnion("type", [
  sectionFor("navbar"),
  sectionFor("hero"),
  sectionFor("about"),
  sectionFor("services"),
  sectionFor("menu"),
  sectionFor("products"),
  sectionFor("gallery"),
  sectionFor("testimonials"),
  sectionFor("faq"),
  sectionFor("stats"),
  sectionFor("opening-hours"),
  sectionFor("location"),
  sectionFor("contact"),
  sectionFor("pricing"),
  sectionFor("cta"),
  sectionFor("timeline"),
  sectionFor("events"),
  sectionFor("team"),
  sectionFor("quote"),
  sectionFor("countdown"),
  sectionFor("gift"),
  sectionFor("wishes"),
  sectionFor("rsvp"),
  sectionFor("blog-latest"),
  sectionFor("links"),
  sectionFor("picks"),
  sectionFor("lessons"),
  sectionFor("footer"),
]);

export type SectionNode = z.infer<typeof sectionSchema>;
export type SectionOf<T extends SectionType> = Extract<SectionNode, { type: T }>;
export type SectionContent<T extends SectionType> = z.infer<(typeof sectionContent)[T]>;

export const hrefSchema = href();
